// ============================================================================
// ZONED TIME — convert between a wall-clock value (as typed in a
// `datetime-local` input) and a UTC instant, for an explicit IANA timezone.
// Mirrors the helpers in supabase/functions/process-campaign/index.ts.
// Intl-only, no dependencies.
// ============================================================================

/** Offset (ms) between `date` rendered in `tz` and the same instant in UTC. */
function zoneOffsetMs(date: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);
  const p: Record<string, string> = {};
  for (const { type, value } of parts) p[type] = value;
  const asUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour === "24" ? "0" : p.hour),
    Number(p.minute),
    Number(p.second),
  );
  return asUtc - date.getTime();
}

/** The browser's IANA timezone, with a safe fallback. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * `"2026-07-29T08:00"` typed for `tz` → the matching UTC instant.
 * Returns null for empty/invalid input.
 */
export function zonedInputToUtc(local: string, tz: string): Date | null {
  if (!local) return null;
  const guess = new Date(`${local.slice(0, 16)}:00Z`);
  if (Number.isNaN(guess.getTime())) return null;
  try {
    const once = new Date(guess.getTime() - zoneOffsetMs(guess, tz));
    // Re-resolve so DST transitions land on the correct offset.
    return new Date(guess.getTime() - zoneOffsetMs(once, tz));
  } catch {
    return guess;
  }
}

/** UTC ISO instant → `"YYYY-MM-DDTHH:mm"` wall clock in `tz`. */
export function utcToZonedInput(iso: string | null, tz: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    const p: Record<string, string> = {};
    for (const { type, value } of new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(d)) {
      p[type] = value;
    }
    const hour = p.hour === "24" ? "00" : p.hour;
    return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`;
  } catch {
    return iso.slice(0, 16);
  }
}

/** Human label for a zone, e.g. "America/New_York (EDT, GMT-4)". */
export function timezoneLabel(tz: string, at: Date = new Date()): string {
  try {
    const name =
      new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
        .formatToParts(at)
        .find((p) => p.type === "timeZoneName")?.value ?? "";
    return name ? `${tz.replace(/_/g, " ")} (${name})` : tz.replace(/_/g, " ");
  } catch {
    return tz;
  }
}
